import React, { useState, useMemo, useRef, useEffect } from 'react';
import DoctorCard from '../DoctorScheduleGenerator/DoctorCard';
import { toolboxService } from '../../../services/api';
import ClinicSearch from '../ClinicSearch';

const Step2_Review = ({ fileData, clid, onBack, onComplete, loading, error, setLoading, setError }) => {
  // Transform file data into the format expected by DoctorCard
  const [doctorDataList, setDoctorDataList] = useState(() => {
    const doctors = fileData?.clinic_doctors || [];
    const schedules = fileData?.clinic_doctor_schedules || [];

    // Create a map of schedules by doctor_id
    const scheduleMap = new Map();
    schedules.forEach(schedule => {
      const doctorId = schedule.doctor_id;
      if (!scheduleMap.has(doctorId)) {
        scheduleMap.set(doctorId, []);
      }
      scheduleMap.get(doctorId).push(schedule);
    });

    // Combine doctors with their schedules
    return doctors.map(doctor => {
      const doctorSchedules = scheduleMap.get(doctor.id) || [];
      // Use the first schedule if available, or create empty schedule
      let schedule = doctorSchedules.length > 0 ? doctorSchedules[0] : {
        from_date: '',
        to_date: '',
        schedule: {},
        is_default_schedule: false
      };

      // If is_default_schedule is true, set from_date and to_date to null
      if (schedule.is_default_schedule === true) {
        schedule = {
          ...schedule,
          from_date: null,
          to_date: null
        };
      }

      return {
        doctor: doctor,
        schedule: schedule
      };
    });
  });

  const [clinicId, setClinicId] = useState(clid || '');
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const abortControllerRef = useRef(null);

  const jsonPreview = useMemo(() => {
    return JSON.stringify(
      doctorDataList.map(data => {
        const schedule = data.schedule || {};
        // Ensure dates are null if is_default_schedule is true
        const processedSchedule = schedule.is_default_schedule === true
          ? {
              ...schedule,
              from_date: null,
              to_date: null
            }
          : schedule;

        return {
          doctor: {
            ...data.doctor,
            clid: clinicId || data.doctor.clid
          },
          schedule: processedSchedule
        };
      }),
      null,
      2
    );
  }, [doctorDataList, clinicId]);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const handleUpdateDoctor = (index, updatedData) => {
    const newList = [...doctorDataList];
    newList[index] = updatedData;
    setDoctorDataList(newList);
  };

  const handleRemoveDoctor = (index) => {
    setDoctorDataList(doctorDataList.filter((_, i) => i !== index));
  };

  const handleImportDoctors = async () => {
    if (!clinicId.trim()) {
      setError('Please enter a Clinic ID');
      return;
    }

    if (doctorDataList.length === 0) {
      setError('No doctors to import');
      return;
    }

    setImporting(true);
    setError('');
    setImportResults(null);

    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    try {
      const doctorsToImport = doctorDataList.map(data => {
        const schedule = data.schedule || {};
        // Ensure dates are null if is_default_schedule is true
        const processedSchedule = schedule.is_default_schedule === true
          ? {
              ...schedule,
              from_date: null,
              to_date: null
            }
          : schedule;

        return {
          doctor: {
            ...data.doctor,
            clid: clinicId
          },
          schedule: processedSchedule
        };
      });

      const result = await new Promise((resolve, reject) => {
        requestAnimationFrame(async () => {
          try {
            const data = await toolboxService.importDoctorsAndSchedules(clinicId, doctorsToImport, signal);
            resolve(data);
          } catch (err) {
            reject(err);
          }
        });
      });

      const createdDoctors = result.created_doctors?.length || 0;
      const updatedDoctors = result.updated_doctors?.length || 0;
      const createdSchedules = result.created_schedules?.length || 0;
      const success = Boolean(result.success || createdDoctors > 0 || updatedDoctors > 0 || createdSchedules > 0);

      if (success) {
        onComplete?.({
          success: true,
          createdDoctors,
          updatedDoctors,
          createdSchedules,
          clid: clinicId || clid
        });
        return;
      }

      setImportResults({
        success: false,
        created_doctors: result.created_doctors || [],
        created_schedules: result.created_schedules || [],
        errors: result.errors || [{ doctor: 'Unknown', error: 'No items imported' }]
      });
      setError('Failed to import doctors. Please check the errors below.');
    } catch (err) {
      console.error('Error importing doctors:', err);
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to import doctors and schedules';
      setError(errorMsg);
      setImportResults({
        success: false,
        created_doctors: [],
        created_schedules: [],
        errors: [{ doctor: 'Unknown', error: errorMsg }]
      });
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Step 2: Review Doctors & Schedules</h2>
        <p className="text-gray-600">Review the doctor information and schedules, then import them</p>
      </div>

      {/* Clinic ID Input with Search */}
      <ClinicSearch
        selectedClinicId={clinicId}
        onClinicSelect={setClinicId}
        error={error}
        setError={setError}
      />

      {/* Doctor Cards */}
      <div>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            Doctors ({doctorDataList.length})
          </h3>
        </div>
        <div className="grid grid-cols-1 gap-6">
          {doctorDataList.map((doctorData, index) => (
            <div key={index} className="relative">
              <DoctorCard
                doctorData={doctorData}
                index={index}
                onUpdate={handleUpdateDoctor}
              />
              <button
                onClick={() => handleRemoveDoctor(index)}
                className="absolute top-4 right-4 text-red-600 hover:text-red-700"
                title="Remove doctor"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </div>
          ))}
        </div>
        {doctorDataList.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            No doctors to review. Please go back and upload a valid file.
          </div>
        )}
      </div>

      {/* Raw JSON Preview */}
      {doctorDataList.length > 0 && (
        <div className="bg-gray-50 p-6 rounded-lg">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Raw JSON Preview</h3>
            <button
              onClick={() => {
                navigator.clipboard.writeText(jsonPreview).then(() => {
                  alert('JSON copied to clipboard!');
                }).catch(err => {
                  console.error('Failed to copy JSON:', err);
                });
              }}
              className="px-3 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              Copy JSON
            </button>
          </div>
          <div className="bg-white border border-gray-300 rounded-md p-4 overflow-auto max-h-96">
            <pre className="text-xs text-gray-800 whitespace-pre-wrap font-mono">
              {jsonPreview}
            </pre>
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="text-red-800 font-medium">{error}</div>
        </div>
      )}

      {/* Import Results */}
      {importResults && (
        <div className="border rounded-lg p-4 bg-yellow-50 border-yellow-200">
          <h4 className="font-semibold mb-2">⚠ Import Failed</h4>
          <div className="text-sm space-y-1">
            <div>Created {importResults.created_doctors.length} doctor(s)</div>
            <div>Created {importResults.created_schedules.length} schedule(s)</div>
            {importResults.errors.length > 0 && (
              <div className="mt-2">
                <div className="font-medium text-yellow-800">Errors ({importResults.errors.length}):</div>
                <ul className="list-disc list-inside text-yellow-700">
                  {importResults.errors.map((err, idx) => (
                    <li key={idx}>{`${err.doctor || 'Unknown doctor'}: ${err.error}`}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Navigation Buttons */}
      <div className="flex justify-between">
        <button
          onClick={onBack}
          disabled={importing}
          className="px-6 py-3 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
        >
          ← Back
        </button>
        <button
          onClick={handleImportDoctors}
          disabled={doctorDataList.length === 0 || !clinicId.trim() || importing || loading}
          className="px-8 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
        >
          {importing ? 'Importing...' : 'Complete & Import Doctors'}
        </button>
      </div>
    </div>
  );
};

export default Step2_Review;
